/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Warns the technician before the capture link's hard expiry — only while
// they're offline (see Capture.jsx's `enabled` computation: an online
// technician can just retry a failing request instead, so there's nothing
// useful this adds there). Every threshold renders the same way (a
// blocking Modal, requiring an explicit tap) — an earlier version tried a
// passive toast for the 30/15-minute marks, but a plain fixed-position
// toast isn't portaled above the rest of the page and could end up
// visually covered by a screen's own UI (e.g. Capturing's camera
// controls), so all four now use the one mechanism that's guaranteed to
// render above everything (see Capture.jsx's Modal usage).
const THRESHOLDS_MINUTES = [30, 15, 5, 1];
const POLL_INTERVAL_MS = 15000;

const shownStorageKey = (token) => `send2me_expiry_reminders_shown_${token}`;

const loadShown = (token) => {
  try {
    const raw = sessionStorage.getItem(shownStorageKey(token));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const saveShown = (token, shownSet) => {
  try {
    sessionStorage.setItem(shownStorageKey(token), JSON.stringify([...shownSet]));
  } catch {
    // Best-effort only — worst case a reminder that was already shown once
    // gets shown again after a reload.
  }
};

const clearShown = (token) => {
  try {
    sessionStorage.removeItem(shownStorageKey(token));
  } catch {
    // No-op — see loadShown.
  }
};

/**
 * @param token
 * @param expiresAt - assignment.expiresAt (Date-parseable)
 * @param createdAt - assignment.createdAt (Date-parseable) — used only to
 *   compute the link's total lifetime, so a threshold that could never
 *   apply to a short-lived link (e.g. a 30-minute warning on a 15-minute
 *   link, the minimum configurable expiry) is never scheduled at all,
 *   rather than firing immediately on load with zero lead time.
 * @param clockOffsetMs - serverTime - Date.now() at last fetch, corrects
 *   for a wrong device clock. Pass 0 if the server hasn't started
 *   returning serverTime yet.
 * @param enabled - reminders are fully inert (no timers, no checks at all)
 *   unless true.
 * @param onExpired - called once (while still enabled) the moment
 *   remaining time hits zero. The caller is responsible for reconciling
 *   with the server once back online — see Capture.jsx.
 */
const useExpiryReminders = ({ token, expiresAt, createdAt, clockOffsetMs = 0, enabled, onExpired }) => {
  const [activeReminder, setActiveReminder] = useState(null); // { minutesRemaining } | null
  const shownRef = useRef(new Set());
  const expiredFiredRef = useRef(false);

  // Reload "already shown" state whenever the token changes — a different
  // link shouldn't inherit thresholds already consumed by another one. In
  // practice the token is stable for the lifetime of a mounted Capture page.
  useEffect(() => {
    shownRef.current = token ? loadShown(token) : new Set();
    expiredFiredRef.current = false;
  }, [token]);

  const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : null;
  const createdAtMs = createdAt ? new Date(createdAt).getTime() : null;

  // Thresholds that could ever apply to this specific link — filters out,
  // for example, a 30-minute warning on a link whose total lifetime is
  // only 15 minutes (the minimum configurable expiry).
  const applicableThresholds = useMemo(() => {
    if (!expiresAtMs || !createdAtMs) return [];
    const totalLifetimeMinutes = (expiresAtMs - createdAtMs) / 60000;
    return THRESHOLDS_MINUTES.filter((t) => t < totalLifetimeMinutes);
  }, [expiresAtMs, createdAtMs]);

  const check = useCallback(() => {
    if (!expiresAtMs) return;
    const remainingMinutes = (expiresAtMs - (Date.now() + clockOffsetMs)) / 60000;

    if (remainingMinutes <= 0) {
      if (!expiredFiredRef.current) {
        expiredFiredRef.current = true;
        // Clear any still-open dismissable reminder as part of this one-time
        // transition — otherwise it stays mounted underneath the
        // undismissable expired alert onExpired triggers below.
        setActiveReminder(null);
        onExpired?.();
      }
      return;
    }

    const candidates = applicableThresholds.filter(
      (t) => !shownRef.current.has(t) && remainingMinutes <= t
    );
    if (candidates.length === 0) return;

    // Fire only the most urgent qualifying tier, and consume every coarser
    // (less urgent) candidate along with it — a technician who was away
    // across several marks (backgrounded tab, or opened the link already
    // deep into its lifetime) gets exactly one accurate reminder instead
    // of a stack of stale ones.
    const toFire = Math.min(...candidates);
    applicableThresholds.filter((t) => t >= toFire).forEach((t) => shownRef.current.add(t));
    saveShown(token, shownRef.current);

    setActiveReminder({
      // Real remaining time, not the nominal threshold label — so a
      // technician opening a link with 8 minutes left sees "~8 minutes",
      // not a stale "15 minutes" that no longer matches reality.
      minutesRemaining: Math.max(1, Math.round(remainingMinutes)),
    });
  }, [expiresAtMs, clockOffsetMs, applicableThresholds, token, onExpired]);

  useEffect(() => {
    if (!enabled) return undefined;
    // Immediate check on enable — this is what catches a threshold already
    // passed by the time the device went offline (or the tab regains focus
    // after being backgrounded across one), not just the next poll tick.
    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, check]);

  const dismissReminder = useCallback(() => setActiveReminder(null), []);

  // Called on a new/handed-off session (see Capture.jsx's
  // handleStartNewSession and the resume-gate landing) so a fresh
  // device/technician doesn't inherit another session's dismissed reminders.
  const resetReminders = useCallback(() => {
    shownRef.current = new Set();
    expiredFiredRef.current = false;
    if (token) clearShown(token);
    setActiveReminder(null);
  }, [token]);

  return { activeReminder, dismissReminder, resetReminders };
};

export default useExpiryReminders;
